import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { readStorage, removeStorage, storageKeys, writeStorage } from '@/lib/storage';

type LaunchContextValue = {
  welcomeSeen: boolean | null;
  setWelcomeSeen: (value: boolean) => Promise<void>;
  refreshWelcomeState: () => Promise<void>;
};

const LaunchContext = createContext<LaunchContextValue | null>(null);

export function LaunchProvider({ children }: { children: ReactNode }) {
  const [welcomeSeen, setWelcomeSeenState] = useState<boolean | null>(null);

  const refreshWelcomeState = useCallback(async () => {
    const stored = readStorage(storageKeys.welcomeSeen);
    setWelcomeSeenState(stored === 'true');
  }, []);

  useEffect(() => {
    let active = true;

    refreshWelcomeState().catch(() => {
      if (active) {
        setWelcomeSeenState(false);
      }
    });

    return () => {
      active = false;
    };
  }, [refreshWelcomeState]);

  const setWelcomeSeen = useCallback(async (value: boolean) => {
    setWelcomeSeenState(value);

    if (value) {
      writeStorage(storageKeys.welcomeSeen, 'true');
    } else {
      removeStorage(storageKeys.welcomeSeen);
    }
  }, []);

  const value = useMemo(
    () => ({ welcomeSeen, setWelcomeSeen, refreshWelcomeState }),
    [refreshWelcomeState, setWelcomeSeen, welcomeSeen]
  );

  return <LaunchContext.Provider value={value}>{children}</LaunchContext.Provider>;
}

export function useLaunchState() {
  const context = useContext(LaunchContext);

  if (!context) {
    throw new Error('useLaunchState must be used within a LaunchProvider');
  }

  return context;
}
